import React, { useEffect, useState } from 'react';
import { Brain, Trash2, Upload, Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { ConvertBrainDialog } from './ConvertBrainDialog';

const STATUS = {
  pending: ['Invited', 'bg-amber-50 text-amber-700 border-amber-200'],
  accepted: ['Signed up', 'bg-blue-50 text-blue-700 border-blue-200'],
  paid: ['Paid customer', 'bg-green-50 text-green-700 border-green-200'],
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SavedBrains = ({ token, onLoad, showOwner = false }) => {
  const [brains, setBrains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [converting, setConverting] = useState(null);

  const onInvited = (id, invite) => setBrains((bs) => bs.map((b) => (b.id === id ? { ...b, invite } : b)));

  useEffect(() => {
    fetch(`${API_URL}/api/demo-brains`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => setBrains(d.brains || []))
      .catch(() => toast.error('Could not load saved brains'))
      .finally(() => setLoading(false));
  }, [token]);

  const remove = async (id) => {
    if (!window.confirm('Delete this saved brain?')) return;
    const res = await fetch(`${API_URL}/api/demo-brains/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return toast.error('Failed to delete');
    setBrains((b) => b.filter((x) => x.id !== id));
    toast.success('Saved brain deleted');
  };

  const filtered = brains.filter((b) => b.business_name.toLowerCase().includes(search.toLowerCase()));

  return (
    <Card className="p-6 bg-white border-slate-200" data-testid="saved-brains-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
          <Brain className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Saved Brains</h2>
          <p className="text-sm text-slate-500">Reload a customer's finished demo in one click</p>
        </div>
      </div>
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      ) : brains.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="saved-brains-empty">No saved brains yet. Use "Save Brain" on the review step.</p>
      ) : (
        <>
          {brains.length > 4 && (
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search saved brains..." className="mb-3 bg-white border-slate-300 text-slate-900" data-testid="saved-brains-search" />
          )}
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {filtered.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors" data-testid={`saved-brain-${b.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-sm text-slate-900 truncate flex items-center gap-2">
                    {b.business_name}
                    {b.invite && STATUS[b.invite.status] && (
                      <span className={`px-2 py-0.5 rounded-full text-[10px] border ${STATUS[b.invite.status][1]}`} data-testid={`brain-status-${b.id}`}>{STATUS[b.invite.status][0]}</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {showOwner && b.owner_name ? `${b.owner_name} · ` : ''}Updated {new Date(b.updated_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {!['accepted', 'paid'].includes(b.invite?.status) && (
                    <button type="button" onClick={() => setConverting(b)} className="px-3 py-1.5 rounded-md text-xs font-medium bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center gap-1" data-testid={`convert-brain-${b.id}`}>
                      <UserPlus className="w-3 h-3" /> {b.invite ? 'Resend' : 'Convert'}
                    </button>
                  )}
                  <button type="button" onClick={() => onLoad(b)} className="px-3 py-1.5 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center gap-1" data-testid={`load-brain-${b.id}`}>
                    <Upload className="w-3 h-3" /> Load
                  </button>
                  <button type="button" onClick={() => remove(b.id)} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-testid={`delete-brain-${b.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {converting && <ConvertBrainDialog brain={converting} token={token} onClose={() => setConverting(null)} onInvited={onInvited} />}
    </Card>
  );
};
